import { Injectable } from '@angular/core';
import { AlertModalComponent } from '../modals/alert-modal/alert-modal.component';
import { ModalController } from '@ionic/angular';
import { FilterModalComponent } from '../modals/filter-modal/filter-modal.component';
import { DocumentCreateModalComponent } from '../modals/document-create-modal/document-create-modal.component';
import { ShareCreateModalComponent } from '../modals/share-create-modal/share-create-modal.component';
import { MailModalComponent } from '../modals/mail-modal/mail-modal.component';
import { ContactModalComponent } from '../modals/contact-modal/contact-modal.component';
import { BankInformationsComponent } from '../modals/bank-informations/bank-informations.component';
import { UserCreateModalComponent } from '../modals/user-create-modal/user-create-modal.component';
import { InsuredExportModalComponent } from '../modals/insured-export-modal/insured-export-modal.component';
import { MemberModalComponent } from '../modals/member-modal/member-modal.component';
import { Insured, Share, User } from '../models/models';
import { modalOpenAnimation } from '../animations/modal-open.animation';
import { modalCloseAnimation } from '../animations/moda-close.animation';
import { ShareModalComponent } from '../modals/share-modal/share-modal.component';
import { UploadDocumentArchiveModalComponent } from '../modals/upload-document-archive-modal/upload-document-archive-modal.component';
import { RadiationModalComponent } from '../modals/radiation-modal/radiation-modal.component';

@Injectable({ providedIn: 'root' })
export class ModalService {
  modal: HTMLIonModalElement = null;

  constructor(private modalCtrl: ModalController) {}

  async createAlertModal(data: any, callback: () => void = null) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: AlertModalComponent,
      componentProps: data,
      cssClass: 'custom auto-height centered size-small',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (
        callback &&
        ((data.actionRequested && response.data?.confirm) ||
          !data.actionRequested)
      ) {
        callback();
      }
    });
    this.modal.present();
  }

  async createFilterModal(data: any, callback: (e) => void = null) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: FilterModalComponent,
      componentProps: data,
      cssClass: 'custom auto-height min-height centered size-small',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (
        callback &&
        ((data.actionRequested && response.data?.confirm) ||
          !data.actionRequested)
      ) {
        callback(response.data.filters);
      }
    });
    this.modal.present();
  }

  async manageDocumentModal(data: any, callback: () => void = null) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: DocumentCreateModalComponent,
      componentProps: data,
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (
        callback &&
        ((data.actionRequested && response.data?.confirm) ||
          !data.actionRequested)
      ) {
        callback();
      }
    });
    this.modal.present();
  }

  async createUserModal(
    data: { save: boolean; actionRequested?: boolean },
    callback: (data: { user: User }) => void = null
  ) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: UserCreateModalComponent,
      componentProps: data,
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (
        callback &&
        ((data?.actionRequested && response.data?.confirm) ||
          !data?.actionRequested)
      ) {
        callback(response.data);
      }
    });
    this.modal.present();
  }

  async createShareDocumentModal(data: any, callback: () => void = null) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: ShareCreateModalComponent,
      componentProps: data,
      cssClass: 'custom auto-height min-height centered size-larger',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (
        callback &&
        ((data.actionRequested && response.data?.confirm) ||
          !data.actionRequested)
      ) {
        callback();
      }
    });
    this.modal.present();
  }

  async createMailModal(data: any, callback: () => void = null) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: MailModalComponent,
      componentProps: data,
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (
        callback &&
        ((data.actionRequested && response.data?.confirm) ||
          !data.actionRequested)
      ) {
        callback();
      }
    });
    this.modal.present();
  }

  async createContactModal() {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: ContactModalComponent,
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
    });
    this.modal.present();
  }

  async createBankInformationsModal() {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: BankInformationsComponent,
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
    });
    this.modal.present();
  }

  async closeAll() {
    let modal = await this.modalCtrl.getTop();
    while (modal) {
      await modal.dismiss();
      modal = await this.modalCtrl.getTop();
    }
  }

  async createExportModal() {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: InsuredExportModalComponent,
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
    });
    this.modal.present();
  }

  async createMemberModal(
    member: Insured,
    callback: () => void = null,
    tab: 'contract' | 'informations' | 'documents' = 'contract'
  ) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: MemberModalComponent,
      componentProps: { member, tab },
      cssClass: 'right-panel xl',
      backdropDismiss: false,
      animated: true,
      enterAnimation: modalOpenAnimation,
      leaveAnimation: modalCloseAnimation,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (callback) {
        callback();
      }
    });
    this.modal.present();
  }

  async openShareModal(share: Share, callback: () => void = null) {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: ShareModalComponent,
      componentProps: { share },
      cssClass: 'right-panel xl',
      backdropDismiss: false,
      animated: true,
      enterAnimation: modalOpenAnimation,
      leaveAnimation: modalCloseAnimation,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
      if (callback) {
        callback();
      }
    });
    this.modal.present();
  }

  async createUploadDocumentArchiveModal() {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.modal = await this.modalCtrl.create({
      component: UploadDocumentArchiveModalComponent,
      componentProps: {},
      cssClass: 'custom auto-height min-height centered size-medium',
      backdropDismiss: false,
    });
    this.modal.onWillDismiss().then((response) => {
      this.modal = null;
    });
    this.modal.present();
  }

  async createRadiationModal(member: Insured, callback: () => void = null) {
    // if (this.modal) {
    //   this.modal.dismiss();
    // }
    const modal = await this.modalCtrl.create({
      component: RadiationModalComponent,
      componentProps: { member },
      cssClass: 'custom auto-height min-height centered size-medium shadowed',
      backdropDismiss: false,
    });
    modal.onWillDismiss().then((response) => {
      if (callback && response.data?.success) {
        callback();
      }
    });
    modal.present();
  }
}
